import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Avatar } from '../primitives';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAccountView } from '../../hooks/useAccountView';
import { useAdminGate } from '../../hooks/useAdminGate';
import { useGuardianMode } from '../../hooks/useGuardianMode';
import { useServerFlag } from '../../hooks/useServerFlag';
import { useSignOutWithConfirm } from '../account/v2/SignOutConfirm';
import { ViewSwitch } from '../account/v2/ViewSwitch';
import { railNavForView } from './athleteNav';
import { BellTrigger } from './BellTrigger';
import { BugReportTrigger } from './BugReportTrigger';
import { WorkspaceBadge } from './WorkspaceBadge';
import { TayroMark } from '../brand/TayroMark';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import { useOpenAthleteMembershipSheet } from '../../contexts/AthleteMembershipSheetContext';
import { RAIL_FOOTER } from '../../lib/coachAthletePaywallCopy';
import styles from './SideNav.module.css';

type NavItem = {
  id: string;
  label: string;
  active?: boolean;
  /**
   * Phase 6b1 PR 8 — when provided, the item renders as a
   * `<Link>` (react-router) and is fully clickable. Items without
   * `href` continue to render as inert `<div>`s (legacy Phase 1
   * default — used by routes that haven't been wired yet).
   *
   * Once all 5 routes (today / plan / races / team / settings)
   * are wired, the optional fallback can be removed.
   */
  href?: string;
  /**
   * CG-4 — optional count badge (the guardian Approvals item). Marine
   * by default; flips to --today on the ACTIVE item (design D2 — the
   * one spot the "now" colour earns its keep on desktop).
   */
  badge?: number;
};

type SideNavProps = {
  items: NavItem[];
  user: { displayName: string; initial: string; weekLabel: string };
  /**
   * CG-4 (design N1) — `'guardian'` renders the reduced rail for
   * guardian surfaces: no "Ask a coach" action, no WorkspaceBadge
   * (a guardian has neither a coach chat nor a team). Default
   * `'athlete'` keeps the existing rail untouched.
   *
   * B76B lane B (handoff screen 1a) — `'coach'` renders the coach-first rail:
   *
   *  · The elevated "Ask a coach" action DISAPPEARS ENTIRELY. ⚖️ The handoff
   *    proposed swapping it for "Ask Rosa"; that proposal was STRUCK at
   *    ratification because Rosa is not conversational. There is no
   *    replacement action and Rosa stays on her existing surfaces (team home,
   *    athlete panel, /approvals) — pinned by test, because "put something
   *    back in the empty slot" is exactly the drive-by this note exists to
   *    stop.
   *  · The WorkspaceBadge STAYS (a coach has a team).
   *  · A quiet "Train yourself" footer row is added above the bell — the ONLY
   *    persistent upsell in the product.
   */
  variant?: 'athlete' | 'guardian' | 'coach';
};

/**
 * Desktop left sidebar: logo + nav items + user block pinned to bottom.
 *
 * **PR 8 update (2026-05-10)**: nav items are now navigable when
 * `href` is set — the cutover-blocker (inert `<div>` items meant
 * users couldn't navigate between routes from any page) is gone.
 * Each consumer route opts in by passing `href: '/v2/<route>'`
 * on the items it knows how to navigate to.
 *
 * Active state is still controlled by the consumer via `active:
 * true` on the matching item — react-router doesn't auto-resolve
 * here because the consumer also passes a `weekLabel` etc. that
 * depends on route-specific state.
 *
 * **W2-CHROME (spec 7.5, `settings_2_0_v1`)**: this entry forks on the flag one
 * component above the original rail, so with the flag off the rail below is
 * mounted exactly as before and none of the new hooks run. With it on, the
 * rail follows the account's chosen VIEW (CH-4, CH-5): the View switch sits
 * where the guardian row was, and the sign-out icon asks first.
 */
export function SideNav(props: SideNavProps) {
  const settingsOn = useServerFlag('settings_2_0_v1');
  return settingsOn ? <SideNavByView {...props} /> : <SideNavRail {...props} />;
}

/** Internal: the flag-on wiring handed to the rail. */
type SideNavAccountView = { requestSignOut: () => void };

/**
 * W2-CHROME — the flag-on rail. The chosen view decides which rail an account
 * sees: when the route's own role fork already agrees (the same variant), the
 * route's items stand untouched (active row, pending badge); when it does not
 * (a dual account in Coaching, a coach-only account that also guards someone,
 * B392) the rail is built from the view, so the desktop nav shows exactly one
 * result per chosen view, the same tab set the bar shows below 1024.
 */
function SideNavByView(props: SideNavProps) {
  const { view } = useAccountView();
  const { pathname } = useLocation();
  const signOutFlow = useSignOutWithConfirm();
  const forView = railNavForView(view, pathname);
  const agrees = (props.variant ?? 'athlete') === forView.variant;
  return (
    <>
      <SideNavRail
        items={agrees ? props.items : forView.items}
        user={agrees ? props.user : { ...props.user, weekLabel: forView.weekLabel }}
        variant={agrees ? props.variant : forView.variant}
        accountView={{ requestSignOut: signOutFlow.requestSignOut }}
      />
      {signOutFlow.dialog}
    </>
  );
}

/**
 * W2-UX-FIX2 (flag on only, `pinned`): the bottom group of the rail (the View switch, the utility rows, the divider and
 * the user row) is ONE pinned box. A day panel makes the page taller than the viewport, and the spacer used to leave
 * the group at the end of the page, off screen; w2-ux-fix1's sticky user row then slid over the View group. The box
 * sits at the bottom of `lowerV2`, the region under the nav items, and `pinnedV2` is sticky inside it: the whole group
 * rests at the viewport bottom while the page scrolls, and it can never rise above the nav items it follows.
 * Flag off: the original spacer and the children as siblings, no wrapper.
 */
function RailBottom({ pinned, children }: { pinned: boolean; children: ReactNode }) {
  if (!pinned) {
    return (
      <>
        <div className={styles.spacer} />
        {children}
      </>
    );
  }
  return (
    <div className={styles.lowerV2}>
      {/* `data-rail-v2` is the hook for the one global rule that holds the scrollbar gutter (styles/global.css). */}
      <div className={styles.pinnedV2} data-rail-v2="">
        {children}
      </div>
    </div>
  );
}

function SideNavRail({
  items,
  user,
  variant = 'athlete',
  accountView,
}: SideNavProps & { accountView?: SideNavAccountView }) {
  const { signOut, user: authUser } = useAuthContext();
  // Admin entry-point (2026-06-16) — the React admin lives at /v2/admin
  // but had no nav affordance. Reuse the same gate the route uses so the
  // link only renders for actual admins; the hook caches per user id, so
  // mounting it in the shared SideNav fires `/api/admin/check` once per
  // session, then resolves from cache on every route change. Non-admins
  // (and signed-out / still-loading auth) get `isAdmin: false` → no link.
  const { isAdmin } = useAdminGate();
  // CG-4 (design N2) — dual-account entry to the guardian world. The
  // SideNav has no account menu (the TopBar's is mobile-only), so the
  // desktop "Switch to guardian view" lives as a footer utility row
  // mirroring the admin-row pattern — rendered ONLY when the user
  // actively guards someone (cached check, useAdminGate-style) and
  // never on the guardian rail itself.
  const { isGuardian } = useGuardianMode();
  const isGuardianRail = variant === 'guardian';
  // B76B (1a) — the coach-first rail.
  const isCoachRail = variant === 'coach';
  // The one persistent upsell. Opens the membership sheet (2b, lane C) through
  // the shared seam; a no-op until that provider is mounted.
  const openMembershipSheet = useOpenAthleteMembershipSheet('rail_footer');
  // CD-ICONS-01 — read once for the rail.
  // ⚠️ The rail's NAV ITEMS are text-only (no glyph slot at all), so
  // `nav.today/team/races/settings` have no mount here — the tab bar is their
  // only one. What this rail does have is two inline <svg>s with exact
  // ratified counterparts, and those are what swap.
  const iconsOn = useIconSystem();
  const navigate = useNavigate();

  // B1 (2026-05-16) — One-click sign-out wired to Supabase. After
  // success, navigate to /landing (basename auto-prepends /v2). The
  // auth state listener in AuthContext then propagates `user = null`
  // to all consumers; route guards that depend on `auth.user` will
  // re-render accordingly. Defensive guards:
  //   - skip if no authUser (button shouldn't be visible in that case,
  //     but a fast double-click could race)
  //   - swallow errors silently (Supabase signOut is local-only after
  //     network revoke; even a network failure clears local state)
  const handleSignOut = async () => {
    if (!authUser) return;
    try {
      await signOut();
    } catch {
      /* clears local state regardless */
    }
    navigate('/landing');
  };

  return (
    <>
      <Link
        to={isGuardianRail ? '/guardian' : '/today'}
        className={styles.logo}
        aria-label="Home"
      >
        <TayroMark size={32} />
        <span className={styles.logoText}>Tayro</span>
      </Link>
      {/* Phase 6 cross-cutting PR 5 — WorkspaceBadge sits between the
          logo and the nav items at Wide+ (≥ 1280 px). Self-gates on
          viewport via CSS @media + on team presence via useTeamHub
          (returns null for solo athletes). Mounted at every tier;
          paints only at Wide+ when a team exists. CG-4: skipped on the
          guardian rail — a guardian has no team. */}
      {!isGuardianRail && <WorkspaceBadge />}
      {/* Elevated "Ask a coach" action (design pass 2026-06-19) — the coaches
          MOAT pinned atop the rail as a marine action, not a 6th equal nav row.
          Desktop's parallel to the mobile CoachFab; routes to /v2/chat.
          CG-4 (N1): dropped on the guardian rail — a guardian has no coach.
          B76B (1a): dropped on the COACH rail too, with NO replacement action
          (the "Ask Rosa" swap was struck at ratification). */}
      {!isGuardianRail && !isCoachRail && (
        <Link to="/chat" className={styles.coachAction} aria-label="Ask a coach">
          {/* CD-ICONS-01 — `nav.chat` (the ratified Bubble). ⚠️ DEVIATION,
              recorded: the slot is 15px on a 16-grid at stroke 1.7, and the
              ratified glyph is a 24-grid at 1.5. It renders at 16 — the
              nearest sanctioned size — so the bubble is a hair larger and a
              hair lighter than today's. The alternative (restroking the glyph
              to 1.7 to match) is exactly what "never restroke per size"
              forbids. Flag OFF is the untouched original <svg>. */}
          <span className={styles.coachGlyph} aria-hidden="true">
            {iconsOn ? (
              <Icon name="nav.chat" size={16} />
            ) : (
              <svg
                width="15"
                height="15"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 9a2 2 0 0 1-2 2H6.5L3.5 13.5V11H4a2 2 0 0 1-2-2V4.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
              </svg>
            )}
          </span>
          <span className={styles.coachText}>
            Ask a coach
            <span className={styles.coachSub}>5 coaches · chat, voice, photo</span>
          </span>
        </Link>
      )}
      <div className={styles.navEyebrow}>Navigate</div>
      <nav className={styles.items}>
        {items.map((item) => {
          const className = [styles.item, item.active ? styles.on : '']
            .filter(Boolean)
            .join(' ');
          const body = (
            <>
              {item.label}
              {typeof item.badge === 'number' && item.badge > 0 && (
                <span className={styles.itemBadge}>{item.badge}</span>
              )}
            </>
          );
          if (item.href && !item.active) {
            return (
              <Link
                key={item.id}
                to={item.href}
                className={className}
                aria-current={item.active ? 'page' : undefined}
              >
                {body}
              </Link>
            );
          }
          // Active item OR no-href: render inert div (active route
          // doesn't navigate to itself; href-less items legacy-pass).
          return (
            <div
              key={item.id}
              className={className}
              aria-current={item.active ? 'page' : undefined}
            >
              {body}
            </div>
          );
        })}
      </nav>
      <RailBottom pinned={Boolean(accountView)}>
      {/* Phase 6 cross-cutting PR 2 + PR 3 — utility rows in the
          SideNav footer at Default+ (per mockup .util pattern in
          cross-cutting.css). Bell + bug-report sit above a 1px
          hairline divider that separates them from the user-card. */}
      <div className={styles.footerSlot}>
        {/* Admin link — discreet utility row, rendered only for admins.
            Styled to match the bell / bug-report footer rows (muted,
            label + glyph). `to="/admin"` resolves to /v2/admin via the
            BrowserRouter basename. */}
        {isAdmin && (
          <Link to="/admin" className={styles.adminRow} aria-label="Admin">
            {/* A2 — `sys.admin` (Shield). R7 optical: the legacy mark is a
                16-grid at stroke 1.6, the ratified one a 24-grid at 1.5, so it
                reads a hair lighter. Adopting the grid is the ruling. */}
            <span className={styles.adminGlyph} aria-hidden="true">
              {iconsOn ? (
                <Icon name="sys.admin" size={16} />
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M8 1.5l5 2v3.5c0 3.2-2.1 5.6-5 6.5-2.9-.9-5-3.3-5-6.5V3.5l5-2z" />
                  <path d="M5.8 8l1.6 1.6L10.4 6.5" />
                </svg>
              )}
            </span>
            <span className={styles.adminLabel}>Admin</span>
          </Link>
        )}
        {/* CG-4 (N2) — "Switch to guardian view": the dual-account entry
            to /guardian. Renders only for users who actively guard
            someone, and never on the guardian rail itself.
            W2-CHROME (flag on): the one View switch takes this slot and
            replaces the row; it renders only with two or more options. */}
        {accountView ? <ViewSwitch variant="rail" /> : null}
        {!accountView && isGuardian && !isGuardianRail && (
          <Link
            to="/guardian"
            className={styles.adminRow}
            aria-label="Switch to guardian view"
          >
            {/* A2 — `sys.role-swap` (Swap). R7 optical, as above. */}
            <span className={styles.adminGlyph} aria-hidden="true">
              {iconsOn ? (
                <Icon name="sys.role-swap" size={16} />
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2.5 5.5h9l-2-2M13.5 10.5h-9l2 2" />
                </svg>
              )}
            </span>
            <span className={styles.adminLabel}>Switch to guardian view</span>
          </Link>
        )}
        {/* B76B (1a CHANGE 2, ratified) — "Train yourself": the one persistent
            upsell, above the bell, in the admin-row pattern. Coach rail only;
            an account that already HAS the athlete side never reaches this
            branch, because `variant="coach"` is passed only where the caller
            has established a coach-first account. Quiet by construction: no
            badge, no colour, no count — a row a coach can ignore forever. */}
        {isCoachRail && (
          <button
            type="button"
            className={styles.adminRow}
            onClick={openMembershipSheet}
          >
            <span className={styles.trainText}>
              <span className={styles.trainLabel}>{RAIL_FOOTER.label}</span>
              <span className={styles.trainSub}>{RAIL_FOOTER.sub}</span>
            </span>
          </button>
        )}
        <BellTrigger />
        <BugReportTrigger />
      </div>
      <div className={styles.footDivider} aria-hidden="true" />
      {/* W2-UX-FIX1 / W2-UX-FIX2 (flag on only, `accountView` is the flag-on wiring): the user row travels with
          the rest of the bottom group inside `RailBottom`'s pinned box, so the View group, the utility rows and
          the user row stay on screen together and never overlap. Flag off: nothing wraps them. */}
      <div className={styles.me}>
        <Avatar initial={user.initial} size={30} />
        <div className={styles.meText}>
          <div className={styles.meName}>{user.displayName}</div>
          <div className={styles.meSub}>{user.weekLabel}</div>
        </div>
        {/* B1 (2026-05-16) — Sign-out is the single discoverable
            "exit the app" affordance on desktop. Lives inline with
            the me-block (right-edge) so it stays visually paired with
            the identity it operates on. Tappable target = 28×28
            (matches BellTrigger / BugReportTrigger sizing). Only
            renders when an auth user exists. */}
        {authUser && (
          <button
            type="button"
            className={accountView ? `${styles.signOutBtn} ${styles.signOutBtnV2}` : styles.signOutBtn}
            aria-label="Sign out"
            title="Sign out"
            onClick={() =>
              accountView ? accountView.requestSignOut() : void handleSignOut()
            }
          >
            {/* CD-ICONS-01 — `sys.sign-out` @16. The button keeps its own
                aria-label ("Sign out") and its own 28×28 target; the glyph is
                decorative. Flag OFF is the untouched original <svg>. */}
            {iconsOn ? (
              <Icon name="sys.sign-out" size={16} />
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3" />
                <path d="M11 11l3-3-3-3" />
                <path d="M14 8H6" />
              </svg>
            )}
          </button>
        )}
      </div>
      </RailBottom>
    </>
  );
}
